import { supabase } from './supabase'

export interface PushSubscriptionData {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

export class PushNotificationService {
  private static vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'DEFAULT_VAPID_KEY'

  // Request notification permission
  static async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications')
      return 'denied'
    }

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission()
      return permission
    }

    return Notification.permission
  }

  // Subscribe to push notifications
  static async subscribeToPush(userId: string): Promise<boolean> {
    try {
  
      
      // Check if service worker is available
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push messaging is not supported in this browser')
        return false
      }

      

      // Request notification permission
      
      const permission = await this.requestPermission()
      
      
      if (permission !== 'granted') {
        console.warn('Notification permission denied')
        return false
      }

      // Get service worker registration
      
      try {
        // Add timeout to prevent hanging
        const registrationPromise = navigator.serviceWorker.ready
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Service worker registration timeout')), 10000)
        )
        
        await Promise.race([registrationPromise, timeoutPromise])

      } catch (error) {
        console.error('Error getting service worker registration:', error)
        // Try alternative approach

        await navigator.serviceWorker.getRegistration() || 
        await navigator.serviceWorker.register('/sw.js')

      }

      // Check if already subscribed in database
      
      const { data: existingSubscription, error: checkError } = await supabase
        .from('push_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single()

      

      if (checkError) {
        if (checkError.code === 'PGRST116') {
  
        } else {
          console.error('Error checking existing subscription:', checkError)
          return false
        }
      } else if (existingSubscription) {

        return true
      }

      // Check browser subscription
      

      

      
      
      
      // For now, let's skip the actual push subscription and just save to database
      // This will allow the UI to work while we set up proper VAPID keys
      
      
      // Create a mock subscription for testing
      const mockSubscriptionData: PushSubscriptionData = {
        endpoint: 'https://mock-endpoint.com/push',
        keys: {
          p256dh: 'mock-p256dh-key',
          auth: 'mock-auth-key'
        }
      }

      
      const { error } = await supabase
        .from('push_subscriptions')
        .insert({
          user_id: userId,
          subscription_data: mockSubscriptionData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })

      if (error) {
        console.error('Error saving push subscription:', error)
        return false
      }

      
      return true
    } catch (error) {
      console.error('Error subscribing to push notifications:', error)
      return false
    }
  }

  // Unsubscribe from push notifications
  static async unsubscribeFromPush(userId: string): Promise<boolean> {
    try {
      if (!('serviceWorker' in navigator)) {
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        const success = await subscription.unsubscribe()
        
        if (success) {
          // Remove subscription from database
          const { error } = await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', userId)

          if (error) {
            console.error('Error removing push subscription from database:', error)
          }
        }

        return success
      }

      return true
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error)
      return false
    }
  }

  // Send local notification (fallback for immediate notifications)
  static async showLocalNotification(
    title: string,
    body: string,
    icon?: string,
    tag?: string,
    data?: any
  ): Promise<void> {
    try {
      const permission = await this.requestPermission()
      if (permission !== 'granted') {
        return
      }

      // If service worker is available, use it for consistent handling
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        await registration.showNotification(title, {
          body,
          icon: icon || '/logo192.png',
          badge: '/logo192.png',
          tag,
          data,
          requireInteraction: false,
          silent: false
        })
      } else {
        // Fallback to basic notification
        const notification = new Notification(title, {
          body,
          icon: icon || '/logo192.png',
          tag
        })

        // Auto-close after 5 seconds
        setTimeout(() => notification.close(), 5000)
      }
    } catch (error) {
      console.error('Error showing local notification:', error)
    }
  }

  // Send push notification via server (placeholder - would need backend implementation)
  static async sendPushNotification(
    userId: string,
    title: string,
    body: string,
    data?: any
  ): Promise<boolean> {
    try {
      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      
      // Generate URL based on notification type
      let url = '/'
      if (data?.type) {
        switch (data.type) {
          case 'task_assigned':
          case 'task_due':
          case 'task_overdue':
          case 'task_completed':
            url = '/tasks'
            break
          case 'event_created':
          case 'event_updated':
          case 'event_reminder':
          case 'rsvp_received':
            url = '/events'
            break
          case 'message_received':
          case 'mention_received':
            url = '/messages'
            break
          case 'group_invitation':
          case 'group_member_joined':
          case 'group_member_left':
            url = '/groups'
            break
          case 'curriculum_shared':
            url = '/curriculum'
            break
          default:
            url = '/'
        }
      }
      
      // Only show local notification if the current user is the target user
      if (currentUser?.id === userId) {
        await this.showLocalNotification(title, body, '/logo192.png', `notification-${Date.now()}`, { ...data, url })
      }
      // Note: In a real implementation, push notifications would be sent to the target user's device
      return true
    } catch (error) {
      console.error('Error sending push notification:', error)
      return false
    }
  }

  // Get push subscription status
  static async getSubscriptionStatus(userId: string): Promise<{ 
    isSubscribed: boolean, 
    permission: NotificationPermission 
  }> {
    try {
      const permission = Notification.permission
      
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return { isSubscribed: false, permission }
      }

      // Check database first
      const { data: dbSubscription } = await supabase
        .from('push_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (dbSubscription) {

        return { isSubscribed: true, permission }
      }

      // Fallback to browser subscription check
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      return { 
        isSubscribed: !!subscription, 
        permission 
      }
    } catch (error) {
      console.error('Error getting subscription status:', error)
      return { isSubscribed: false, permission: 'denied' }
    }
  }

  // Utility function to convert VAPID key
  private static urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  // Test notification (for debugging)
  static async testNotification(): Promise<void> {
    
    try {
      await this.showLocalNotification(
        'Test Notification',
        'This is a test notification from SALT App',
        '/logo192.png',
        'test-notification'
      )
      
    } catch (error) {
      console.error('Error sending test notification:', error)
      throw error
    }
  }

  // Debug function to check database subscriptions
  static async debugSubscriptions(userId: string): Promise<void> {
    
    try {
      await supabase
        .from('push_subscriptions')
        .select('*')
        .eq('user_id', userId)

      
    } catch (error) {
      console.error('Error debugging subscriptions:', error)
    }
  }
} 